import React from 'react'
import { useHistory } from 'react-router-dom'
import { ClockIcon, GlobeAltIcon } from '@heroicons/react/solid'

import { useGlobalState } from '@/state'
import PublicDemoRow from '@/pages/Users/PublicProfile/PublicDemoRow'
import PublicEducationRow from '@/pages/Users/PublicProfile/PublicEducationRow'
import PublicRecommendations from '@/pages/Users/PublicProfile/PublicRecommendations'
import PublicCredits from '@/pages/Users/PublicProfile/PublicCredits'
import PublicSubmissionRow from '@/pages/Users/PublicProfile/PublicSubmissionRow'
import Section from '@/pages/Users/PublicProfile/Section'

const plain = (html) => (html || '').replace(/<[^>]*>/g, '').trim()

const Chips = ({ items }) => (
  <div className='flex flex-wrap gap-1.5'>
    { items.map((l) => <span key={l.id} className='rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-gray-800 dark:bg-gray-700 dark:text-gray-100'>{l.name}</span>) }
  </div>
)

const PublicProfileGeneral = () => {
  const [profileContainer] = useGlobalState('profileContainer')
  const history = useHistory()
  const { user, demos, showcase, educations, genderAges, accents, languages, skills, interests, hardwares, softwares } = profileContainer
  const showcased = showcase?.submissions || []
  const voice = [...(genderAges || []), ...(accents || []), ...(languages || [])]
  const craft = [...(skills || []), ...(interests || [])]
  const gear = [...(hardwares || []), ...(softwares || [])]

  // @mentions inside rich text route through the SPA instead of reloading.
  const handleContentClick = (e) => {
    if (!e.target.closest('a')) { return }
    if (e.target.getAttribute('data-mention') === 'mention') {
      e.preventDefault()
      history.push('/' + e.target.getAttribute('data-to'))
    }
  }

  const hasWork = showcased.length > 0 || (demos || []).length > 0
  const hasRates = plain(user.pricing) || plain(user.lookingFor)
  const empty = !hasWork && !plain(user.description) && !hasRates && voice.length === 0 && craft.length === 0

  return <div className='space-y-4'>
    { plain(user.description) && <Section title={`About ${user.displayName}`}>
      <div onClick={handleContentClick} className='prose prose-sm max-w-none text-gray-700 dark:prose-invert dark:text-gray-300' dangerouslySetInnerHTML={{ __html: user.description }} />
    </Section> }

    { hasWork && <Section title='Work'>
      <div className='space-y-3'>
        { showcased.length > 0 && <ul className='space-y-3'>
          { showcased.map((sub) => <PublicSubmissionRow key={`show${sub.id}`} submission={sub} voteIds={showcase.voteIds || []} compact />) }
        </ul> }
        { (demos || []).map((demo) => <PublicDemoRow key={`demo${demo.id}`} demo={demo} />) }
      </div>
    </Section> }

    { hasRates && <Section title='Working together'>
      <div className='grid gap-4 sm:grid-cols-2'>
        { plain(user.pricing) && <div>
          <h3 className='text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>Rates</h3>
          <div onClick={handleContentClick} className='prose prose-sm mt-1 max-w-none text-gray-700 dark:prose-invert dark:text-gray-300' dangerouslySetInnerHTML={{ __html: user.pricing }} />
        </div> }
        { plain(user.lookingFor) && <div>
          <h3 className='text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>Looking for</h3>
          <div onClick={handleContentClick} className='prose prose-sm mt-1 max-w-none text-gray-700 dark:prose-invert dark:text-gray-300' dangerouslySetInnerHTML={{ __html: user.lookingFor }} />
        </div> }
      </div>
      <div className='mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400'>
        { user.timezone && <span className='inline-flex items-center gap-x-1'><ClockIcon className='h-4 w-4' />{user.timezone.replace(/_/g, ' ')}</span> }
        { user.isAvailableNonUnion && <span className='inline-flex items-center gap-x-1'><GlobeAltIcon className='h-4 w-4' />Open to non-union work</span> }
      </div>
    </Section> }

    { (voice.length > 0 || craft.length > 0 || gear.length > 0) && <Section title='Voice and skills'>
      <div className='space-y-3'>
        { voice.length > 0 && <div><h3 className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>Voice</h3><Chips items={voice} /></div> }
        { craft.length > 0 && <div><h3 className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>Skills and interests</h3><Chips items={craft} /></div> }
        { gear.length > 0 && <div><h3 className='mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>Setup</h3><Chips items={gear} /></div> }
      </div>
    </Section> }

    <PublicRecommendations />
    <PublicCredits />

    { (educations || []).length > 0 && <Section title='Training'>
      <div className='space-y-3'>
        { educations.map((edu) => <PublicEducationRow key={`edu${edu.id}`} education={edu} />) }
      </div>
    </Section> }

    { empty && <div className='rounded-xl border-2 border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400'>
      {user.displayName} hasn't filled out their profile yet. Check back, or just message them.
    </div> }
  </div>
}

export default PublicProfileGeneral
